import React, { useMemo } from 'react';
import { Dumbbell, Lightbulb } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSessions } from '../../hooks/useSessions';
import { BeachvisionLayout } from '../../components/beachvision/BeachvisionLayout';
import { PageErrorBoundary } from '../../components/beachvision/PageErrorBoundary';

function TrainingInner() {
  const { data: sessions } = useSessions();

  const latestSession = sessions?.[0];
  const insight =
    latestSession?.status === 'complete' && latestSession?.overall_score != null
      ? 'Keep building on your last analyzed session.'
      : 'Upload footage to unlock AI coaching insights.';

  const drills = useMemo(
    () => [
      { title: 'Serve mechanics', sets: '3 × 15 reps', difficulty: 'Medium' },
      { title: 'Block footwork', sets: '4 × 10 reps', difficulty: 'Hard' },
      { title: 'Sideout execution', sets: '3 × 8 reps', difficulty: 'Medium' },
    ],
    []
  );

  return (
    <>
      <header className="px-4 pt-6 pb-4 flex items-center gap-3">
        <Dumbbell className="h-5 w-5 text-beachvision-coral" />
        <h1 className="font-display font-black text-xl tracking-tight text-beachvision-primary-light">
          Training
        </h1>
      </header>

      <section className="px-4 space-y-4">
        {/* Next goal card */}
        <div className="rounded-2xl bg-beachvision-surface-low p-5">
          <span className="inline-block rounded-full bg-beachvision-teal/25 text-beachvision-teal text-xs font-display font-bold uppercase px-3 py-1 tracking-wide">
            Next goal
          </span>
          <p className="text-xs text-beachvision-muted mt-3">Based on your activity</p>
          <h3 className="font-display font-bold text-white text-lg mt-1">
            Consistent sideout execution
          </h3>
          <div className="flex items-start gap-2 mt-3 text-sm text-beachvision-teal">
            <Lightbulb className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{insight}</span>
          </div>
          <button
            type="button"
            className="mt-4 w-full bg-beachvision-cta text-beachvision-on-coral rounded-xl px-4 py-3 font-display font-bold text-sm"
            onClick={() => toast('Coming soon', { icon: '🏋️' })}
          >
            Generate training program
          </button>
        </div>

        {/* Suggested drills */}
        <div>
          <p className="text-xs font-display font-bold uppercase text-beachvision-muted tracking-wide mb-3">
            Suggested drills
          </p>
          <div className="space-y-3">
            {drills.map((d) => (
              <div
                key={d.title}
                className="rounded-2xl bg-beachvision-surface-low p-4 flex items-center justify-between"
              >
                <div>
                  <p className="font-display font-bold text-white text-sm">{d.title}</p>
                  <p className="text-beachvision-muted text-xs mt-0.5">{d.sets}</p>
                </div>
                <span
                  className={`text-xs font-display font-bold uppercase px-3 py-1 rounded-full ${
                    d.difficulty === 'Hard'
                      ? 'bg-beachvision-coral/20 text-beachvision-coral'
                      : 'bg-beachvision-teal/20 text-beachvision-teal'
                  }`}
                >
                  {d.difficulty}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default function TrainingPage() {
  return (
    <BeachvisionLayout>
      <PageErrorBoundary>
        <TrainingInner />
      </PageErrorBoundary>
    </BeachvisionLayout>
  );
}
